import { useMemo } from 'react';
import { Box, Flex, Select, Text, useColorModeValue } from '@chakra-ui/react';
import Card from 'components/card/Card';
import PieChart from 'components/charts/PieChart';
import { VSeparator } from 'components/separator/Separator';
import { useFinance } from 'contexts/FinanceContext';
import { pieChartOptions } from 'variables/charts';
import { TransactionType } from 'types/finance';

const HORIZON_COLORS = ['#4318FF', '#6AD2FF', '#EFF4FB'];

interface CategoryPieCardProps {
  type?: TransactionType;
  title?: string;
  [x: string]: any;
}

export default function CategoryPieCard({
  type = 'expense',
  title = 'Your Pie Chart',
  ...rest
}: CategoryPieCardProps) {
  const { expenseByCategory, incomeByCategory, totals } = useFinance();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardColor = useColorModeValue('white', 'navy.700');
  const cardShadow = useColorModeValue(
    '0px 18px 40px rgba(112, 144, 176, 0.12)',
    'unset'
  );

  const source = type === 'expense' ? expenseByCategory : incomeByCategory;
  const totalAmount =
    type === 'expense' ? totals.totalExpense : totals.totalIncome;

  const sorted = useMemo(
    () => [...source].sort((a, b) => b.amount - a.amount),
    [source]
  );

  const topThree = useMemo(() => {
    const top = sorted.slice(0, 3);
    if (sorted.length <= 3) return top;
    const otherAmount = sorted.slice(3).reduce((s, c) => s + c.amount, 0);
    return [...top, { name: 'Other', amount: otherAmount, color: '#EFF4FB' }];
  }, [sorted]);

  const pieChartData = useMemo(() => {
    if (topThree.length === 0) return [100];
    return topThree.map((c) => c.amount);
  }, [topThree]);

  const chartOptions = useMemo(() => {
    if (topThree.length === 0) {
      return {
        ...pieChartOptions,
        labels: ['No data'],
        colors: ['#EFF4FB'],
        fill: { colors: ['#EFF4FB'] },
      };
    }
    return {
      ...pieChartOptions,
      labels: topThree.map((c) => c.name),
      colors: HORIZON_COLORS.slice(0, topThree.length),
      fill: { colors: HORIZON_COLORS.slice(0, topThree.length) },
    };
  }, [topThree]);

  const getPercent = (amount: number) => {
    if (totalAmount === 0) return '0%';
    return `${Math.round((amount / totalAmount) * 100)}%`;
  };

  const legendItems =
    topThree.length > 0
      ? topThree.slice(0, 2)
      : [
          { name: type === 'expense' ? 'Expenses' : 'Income', amount: 0 },
          { name: 'Other', amount: 0 },
        ];

  return (
    <Card p="20px" alignItems="center" flexDirection="column" w="100%" {...rest}>
      <Flex
        px={{ base: '0px', '2xl': '10px' }}
        justifyContent="space-between"
        alignItems="center"
        w="100%"
        mb="8px"
      >
        <Text color={textColor} fontSize="md" fontWeight="600" mt="4px">
          {title}
        </Text>
        <Select
          fontSize="sm"
          variant="subtle"
          defaultValue="monthly"
          width="unset"
          fontWeight="700"
        >
          <option value="daily">Daily</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </Select>
      </Flex>

      <Box w="100%" minH="280px" h="280px" display="flex" alignItems="center" justifyContent="center">
        <PieChart
          h="100%"
          w="100%"
          height="280px"
          chartData={pieChartData}
          chartOptions={chartOptions}
        />
      </Box>

      <Card
        bg={cardColor}
        flexDirection="row"
        boxShadow={cardShadow}
        w="100%"
        p="15px"
        px="20px"
        mt="15px"
        mx="auto"
      >
        {legendItems.map((cat, index) => (
          <Flex key={`${cat.name}-${index}`} flex="1" align="center">
            {index > 0 && (
              <VSeparator mx={{ base: '60px', xl: '60px', '2xl': '60px' }} />
            )}
            <Flex direction="column" py="5px" me={index === 0 ? '0' : '10px'}>
              <Flex align="center">
                <Box
                  h="8px"
                  w="8px"
                  bg={HORIZON_COLORS[index] ?? '#EFF4FB'}
                  borderRadius="50%"
                  me="4px"
                />
                <Text
                  fontSize="xs"
                  color="secondaryGray.600"
                  fontWeight="700"
                  mb="5px"
                  isTruncated
                >
                  {cat.name}
                </Text>
              </Flex>
              <Text fontSize="lg" color={textColor} fontWeight="700">
                {getPercent(cat.amount)}
              </Text>
            </Flex>
          </Flex>
        ))}
      </Card>
    </Card>
  );
}
